import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditPurchaseModal = ({ isOpen, onClose, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/credits/packages`);
      const data = await response.json();
      if (data.success) {
        setPackages(data.packages);
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load credit packages');
    }
  };

  const handlePurchase = async (pkg) => {
    setLoading(true);
    setSelectedPackage(pkg.id);

    try {
      const token = localStorage.getItem('token');
      
      // Create order
      const orderResponse = await fetch(`${BACKEND_URL}/api/credits/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ package: pkg.id })
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error('Failed to create order');
      }

      // Razorpay payment
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Nerd AI',
        description: `${pkg.credits} Nerd-AI Credits`,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${BACKEND_URL}/api/credits/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              toast.success(`Successfully purchased ${pkg.credits} credits!`);
              window.dispatchEvent(new Event('creditUpdated')); // Trigger live update
              onSuccess();
              onClose();
            } else {
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Failed to verify payment');
          } finally {
            setLoading(false);
            setSelectedPackage(null);
          }
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setSelectedPackage(null);
          }
        },
        theme: {
          color: '#9333ea'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to initiate purchase');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="h-6 w-6" />
              Buy Nerd-AI Credits
            </h2>
            <p className="text-purple-100 text-sm mt-1">Power your learning journey with AI</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Packages */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="border-2 border-purple-200 rounded-xl p-4 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    💎 {pkg.credits}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">Credits</div>
                  
                  <div className="bg-purple-50 rounded-lg p-3 mb-3">
                    <div className="text-xs text-gray-600">Base Price</div>
                    <div className="text-lg font-semibold text-gray-900">₹{pkg.base_price}</div>
                    <div className="text-xs text-gray-600 mt-1">+ GST ₹{pkg.gst}</div>
                    <div className="text-sm font-bold text-purple-600 mt-2">
                      Total: ₹{pkg.total}
                    </div>
                  </div>

                  <Button
                    onClick={() => handlePurchase(pkg)}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  >
                    {loading && selectedPackage === pkg.id ? 'Processing...' : 'Buy Now'}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Info */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How to use credits:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Ask Nerd AI (Exam Practice): 3 credits per query</li>
              <li>• Ask Nerd AI (Dashboard): 2 credits per query</li>
              <li>• Research Paper Analysis: 70 credits per paper</li>
              <li>• Credits valid for 30 days from purchase</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;
